/**
 * Sammel-Erledigung mit Vorschau (Aufgabenverwaltung 2.0): erst alle betroffenen Aufgaben mit Fristen, Unteraufgaben und Verknüpfungen zeigen,
 * dann nur nach Bestätigung erledigen. Fristen und Unteraufgaben bleiben unverändert; Teilfehler werden als solche angezeigt.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, CheckCheck, Loader2, XCircle, CheckCircle2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { betreuungAPI } from '@/api/betreuungAPI';

export default function SammelErledigenDialog({ open, ids, onClose, onDone }) {
  const [vorschau, setVorschau] = useState(null);
  const [fehler, setFehler] = useState(null);
  const [laeuft, setLaeuft] = useState(false);
  const [ergebnis, setErgebnis] = useState(null);

  useEffect(() => {
    if (!open) return;
    setVorschau(null); setFehler(null); setErgebnis(null);
    betreuungAPI.sammelErledigenVorschau(ids).then(setVorschau).catch(e => setFehler(e.message));
  }, [open, ids]);

  const bestaetigen = async () => {
    setLaeuft(true);
    try {
      const staende = Object.fromEntries(vorschau.aufgaben.map(a => [a.id, a.stand]));
      setErgebnis(await betreuungAPI.sammelErledigen(staende));
    } catch (e) {
      setFehler(e.message);
    } finally {
      setLaeuft(false);
    }
  };

  const schliessen = () => { if (ergebnis) onDone?.(ergebnis); else onClose?.(); };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) schliessen(); }}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="sammel-erledigen-dialog">
        <DialogHeader>
          <DialogTitle>Aufgaben als erledigt markieren</DialogTitle>
          <DialogDescription>Vorschau – es wurde noch nichts geändert. Verknüpfte Fristen und Unteraufgaben werden nicht erledigt.</DialogDescription>
        </DialogHeader>
        {fehler && <p className="text-sm text-red-600" data-testid="sammel-fehler">{fehler}</p>}
        {!vorschau && !fehler && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>}
        {vorschau && !ergebnis && (
          <div className="space-y-3">
            {vorschau.warnungen.map(w => (
              <p key={w} className="flex gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-2"><AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />{w}</p>
            ))}
            {vorschau.klienten > 1 && <p className="text-xs text-slate-500">Die Auswahl betrifft {vorschau.klienten} Klienten.</p>}
            <ul className="space-y-2">
              {vorschau.aufgaben.map(a => (
                <li key={a.id} className={`border rounded p-2 text-sm ${a.kritisch ? 'border-amber-300 bg-amber-50/40' : 'border-slate-200'}`} data-testid={`sammel-zeile-${a.id}`}>
                  <div className="font-medium text-slate-800">{a.titel}</div>
                  <div className="text-xs text-slate-500">{[a.klient_name, a.status, a.faellig && `fällig ${new Date(a.faellig).toLocaleDateString('de-DE')}`,
                    a.dokumente ? `${a.dokumente} Dokument(e)` : null].filter(Boolean).join(' · ')}</div>
                  {a.hinweise.map(h => <div key={h} className="text-xs text-amber-800 mt-0.5">⚠ {h}</div>)}
                </li>
              ))}
            </ul>
          </div>
        )}
        {ergebnis && (
          <div className="space-y-2" data-testid="sammel-ergebnis">
            <p className={`text-sm font-medium ${ergebnis.vollstaendig ? 'text-green-700' : 'text-red-700'}`}>
              {ergebnis.vollstaendig ? 'Erledigt' : 'Teilweise erledigt'}: {ergebnis.erledigt} geändert, {ergebnis.unveraendert} unverändert, {ergebnis.fehler} nicht ausgeführt
              (aus der Datenbank zurückgelesen).
            </p>
            <ul className="space-y-1 text-sm">
              {ergebnis.ergebnisse.map(e => (
                <li key={e.id} className="flex gap-2">
                  {e.ok ? <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" /> : <XCircle className="w-4 h-4 text-red-600 flex-shrink-0" />}
                  <span>{e.titel}{e.hinweis ? ` – ${e.hinweis}` : ''}{e.fehler ? ` – ${e.fehler}` : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <DialogFooter>
          {!ergebnis && <Button variant="outline" onClick={onClose}>Abbrechen</Button>}
          {!ergebnis && vorschau && (
            <Button onClick={bestaetigen} disabled={laeuft || vorschau.zu_erledigen === 0} data-testid="sammel-bestaetigen">
              {laeuft ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <CheckCheck className="w-4 h-4 mr-1" />}
              {vorschau.zu_erledigen} Aufgabe(n) erledigen
            </Button>
          )}
          {ergebnis && <Button onClick={schliessen} data-testid="sammel-schliessen">Schließen</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
